import React, { useState, useEffect } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableHead, TableRow, 
  Chip, Button, CircularProgress, Alert, Dialog, DialogTitle, 
  DialogContent, DialogActions, Typography, Grid, Divider
} from '@mui/material';
import api from '../../api';

const PayslipApprovals = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });
  
  // Summary Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const res = await api.get('/payroll/payslips/batches');
      setBatches(res.data || []);
    } catch (err) {
      showNotif('Failed to load pending batches.', 'error');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchBatches(); }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  const handleReviewClick = async (batchId) => {
    setSelectedBatchId(batchId);
    setSummaryLoading(true);
    setDialogOpen(true);
    try {
      const res = await api.get(`/payroll/payslips/batches/${batchId}/summary`);
      setSummaryData(res.data);
    } catch (err) {
      showNotif('Failed to load batch summary.', 'error');
      setDialogOpen(false);
    } finally { setSummaryLoading(false); }
  };

  const handleProcessBatch = async (action) => {
    setSummaryLoading(true);
    try {
      await api.post(`/payroll/payslips/batches/${selectedBatchId}/process`, { 
        action, 
        reason: action === 'REJECT' ? 'Rejected by Admin' : null 
      });
      showNotif(`Batch ${action === 'APPROVE' ? 'Approved' : 'Rejected'} successfully.`);
      setDialogOpen(false);
      fetchBatches(); // Refresh table
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Action failed.', 'error');
    } finally { setSummaryLoading(false); }
  };

  const renderBankStat = (label, count, total) => {
    const isComplete = count === total;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {count} / {total}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: isComplete ? 'success.main' : 'error.main' }}>
            {isComplete ? '(OK)' : '(MISSING)'}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}
      
      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell><b>Batch Info</b></TableCell>
              <TableCell><b>Employees</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell align="center"><b>Admin Action</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map(b => (
              <TableRow key={b.id}>
                <TableCell>{b.payroll_month} {b.payroll_year}<br/><Typography variant="caption">{new Date(b.generated_at).toLocaleString()}</Typography></TableCell>
                <TableCell>{b.total_employees} Payslips</TableCell>
                <TableCell>
                  <Chip label={b.approval_status} size="small" color={b.approval_status === 'Pending Approval' ? 'warning' : (b.approval_status === 'Approved' ? 'success' : 'error')} />
                </TableCell>
                <TableCell align="center">
                  {b.approval_status === 'Pending Approval' ? (
                    <Button variant="contained" color="primary" size="small" onClick={() => handleReviewClick(b.id)}>Review & Process</Button>
                  ) : (
                    <Typography variant="caption" color="textSecondary">Processed</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {batches.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>No batches found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}

      {/* SUMMARY & APPROVAL DIALOG */}
      <Dialog open={dialogOpen} onClose={() => !summaryLoading && setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Review Payslip Batch Summary</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {summaryLoading && !summaryData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : summaryData ? (
            <Box>
              {/* TOP SECTION: Left/Right Stats */}
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" color="primary" sx={{ mb: 2 }}>Generate for {summaryData.month} {summaryData.year}</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>Total Employees: <b>{summaryData.total_employees}</b></Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Payroll Status:</Typography>
                  <Typography variant="body2">Processed: <b>{summaryData.payroll_status.processed}</b></Typography>
                  <Typography variant="body2" color="error">Hold Salary: <b>{summaryData.payroll_status.hold}</b></Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Employee Status:</Typography>
                  <Typography variant="body2">Active: <b>{summaryData.employee_status.active}</b></Typography>
                  <Typography variant="body2" color="warning.main">Notice: <b>{summaryData.employee_status.notice}</b></Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>Bank Details Validation</Typography>
                  <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    {renderBankStat("Bank Name", summaryData.banking_stats.bank_name, summaryData.total_employees)}
                    {renderBankStat("Account Holder", summaryData.banking_stats.account_holder, summaryData.total_employees)}
                    {renderBankStat("Account Number", summaryData.banking_stats.account_number, summaryData.total_employees)}
                    {renderBankStat("Branch Name", summaryData.banking_stats.branch_name, summaryData.total_employees)}
                    {renderBankStat("IFSC Code", summaryData.banking_stats.ifsc_code, summaryData.total_employees)}
                    {renderBankStat("PAN Number", summaryData.banking_stats.pan_number, summaryData.total_employees)}
                    {renderBankStat("PF Number", summaryData.banking_stats.pf_number, summaryData.total_employees)}
                  </Box>
                </Grid>
              </Grid>

              {/* MIDDLE SECTION: Financial Totals Table */}
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>Salary Details (Totals)</Typography>
              <Table size="small" sx={{ mb: 4, border: '1px solid #e2e8f0' }}>
                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                  <TableRow>
                    <TableCell><b>Salary Component</b></TableCell>
                    <TableCell align="right"><b>Actual</b></TableCell>
                    <TableCell align="right"><b>Earned</b></TableCell>
                    <TableCell align="right"><b>Difference</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryData.financial_totals.map((row, idx) => (
                    <TableRow key={idx} sx={{ bgcolor: row.name === 'Gross' || row.name === 'Net' ? '#f8fafc' : 'inherit' }}>
                      <TableCell sx={{ fontWeight: row.name === 'Gross' || row.name === 'Net' ? 'bold' : 'normal' }}>{row.name}</TableCell>
                      <TableCell align="right">{row.actual.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.earned.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: row.diff > 0 ? 'error.main' : 'inherit' }}>{row.diff.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: '#1e293b' }}>
                    <TableCell colSpan={3} sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Grand Total Difference:</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {summaryData.financial_totals.reduce((sum, row) => sum + row.diff, 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* BOTTOM SECTION: Additions & Deductions */}
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>Additions</Typography>
                  <Table size="small" sx={{ border: '1px solid #e2e8f0' }}>
                    <TableHead sx={{ bgcolor: '#f0fdf4' }}>
                      <TableRow>
                        <TableCell><b>Component</b></TableCell>
                        <TableCell align="right"><b>Total</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(summaryData.additions).length > 0 ? (
                        Object.entries(summaryData.additions).map(([key, val]) => (
                          <TableRow key={key}>
                            <TableCell>{key}</TableCell>
                            <TableCell align="right">{val.toLocaleString()}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={2} align="center">No active additions.</TableCell></TableRow>
                      )}
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell><b>Total Additions:</b></TableCell>
                        <TableCell align="right"><b>{Object.values(summaryData.additions).reduce((a, b) => a + b, 0).toLocaleString()}</b></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>Deductions</Typography>
                  <Table size="small" sx={{ border: '1px solid #e2e8f0' }}>
                    <TableHead sx={{ bgcolor: '#fef2f2' }}>
                      <TableRow>
                        <TableCell><b>Component</b></TableCell>
                        <TableCell align="right"><b>Total</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(summaryData.deductions).length > 0 ? (
                        Object.entries(summaryData.deductions).map(([key, val]) => (
                          <TableRow key={key}>
                            <TableCell>{key}</TableCell>
                            <TableCell align="right">{val.toLocaleString()}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow><TableCell colSpan={2} align="center">No active deductions.</TableCell></TableRow>
                      )}
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell><b>Total Deductions:</b></TableCell>
                        <TableCell align="right"><b>{Object.values(summaryData.deductions).reduce((a, b) => a + b, 0).toLocaleString()}</b></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>

            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
          <Button onClick={() => handleProcessBatch('REJECT')} variant="outlined" color="error" disabled={summaryLoading}>
            {summaryLoading ? <CircularProgress size={24} /> : 'Reject & Unlock Payroll'}
          </Button>
          <Box>
            <Button onClick={() => setDialogOpen(false)} disabled={summaryLoading} sx={{ mr: 2 }}>Close</Button>
            <Button onClick={() => handleProcessBatch('APPROVE')} variant="contained" color="success" disabled={summaryLoading}>
              {summaryLoading ? <CircularProgress size={24} /> : 'Approve Batch'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayslipApprovals;